import { Box, CircularProgress, Typography } from '@mui/material';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { getAPIErrorMessage } from '../../helpers/helpers';
import { PullDataService } from '../../service/PullDataService';
import { useAuthStore } from '../../store/storeAuth';
import {
  NotificationType,
  useNotificationStore,
} from '../../store/storeNotifications';
import SearchableSelect from '../SearchableSelect';
import Label from '../UI/Label/Label';

const LoadingPlaceholder = ({
  theme,
  label,
}: {
  theme?: string;
  label: string;
}) => (
  <Box sx={{ display: 'flex', alignItems: 'center', gap: '10px', py: '8px' }}>
    <CircularProgress
      size={16}
      sx={{ color: theme === 'dark' ? '#D0D5DD' : '#667085' }}
    />
    <Typography
      sx={{ fontSize: '13px', color: theme === 'dark' ? '#98A2B3' : '#667085' }}
    >
      {label}
    </Typography>
  </Box>
);

interface MicrosoftFile {
  id: string;
  name: string;
}

export interface MicrosoftFileSelectProps {
  fileType: 'docx' | 'pptx';
  fileIdField: string;
  label: string;
  selectLabel: string;
  onChangeSelect: (e: { target: { name: string; value: any } }) => void;
  data: any;
  children?: React.ReactNode;
}

const DEBOUNCE_MS = 400;

const MicrosoftFileSelect: React.FC<MicrosoftFileSelectProps> = ({
  fileType,
  fileIdField,
  label,
  selectLabel,
  onChangeSelect,
  data,
  children,
}) => {
  const { user } = useAuthStore(state => state);
  const setNotification = useNotificationStore(state => state.setNotification);

  const [files, setFiles] = useState<MicrosoftFile[]>([]);
  const [selectedFileId, setSelectedFileId] = useState<string>(
    data?.[fileIdField] ?? '',
  );
  const [loadingFiles, setLoadingFiles] = useState(false);
  const [initialLoadDone, setInitialLoadDone] = useState(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const loadFileFn =
    fileType === 'docx'
      ? PullDataService.getMicrosoftWordFiles
      : PullDataService.getMicrosoftPowerPointFiles;

  const loadFiles = useCallback(
    async (search?: string) => {
      setLoadingFiles(true);
      try {
        const response = await loadFileFn(search ? { search } : undefined);
        setFiles(response.data.files ?? []);
      } catch (error) {
        setNotification({
          type: NotificationType.Error,
          message: getAPIErrorMessage(error),
        });
      } finally {
        setLoadingFiles(false);
        setInitialLoadDone(true);
      }
    },
    [loadFileFn, setNotification],
  );

  useEffect(() => {
    loadFiles();
  }, [loadFiles]);

  const handleInputChange = (
    _event: React.SyntheticEvent,
    inputValue: string,
    reason: string,
  ) => {
    if (reason !== 'input' && reason !== 'clear') return;
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      loadFiles(inputValue || undefined);
    }, DEBOUNCE_MS);
  };

  const fileOptions = files.map(f => ({ label: f.name, value: f.id }));

  const searchableSelectSx = {
    '& .MuiList-root': {
      backgroundColor: user?.theme === 'dark' ? '#101828' : '#F9FAFB',
    },
    '& .MuiSvgIcon-root': {
      color: user?.theme === 'dark' ? '#D0D5DD' : '#101828',
    },
    borderRadius: '8px',
    border: 'none',
    boxShadow: 'none',
    '.MuiOutlinedInput-notchedOutline': { border: 0 },
  };

  const menuPropsSx = {
    PaperProps: {
      sx: {
        '& .Mui-selected': {
          backgroundColor: user?.theme === 'dark' ? '#667085' : '#D0D5DD',
        },
        backgroundColor: user?.theme === 'dark' ? '#1D2939' : '#F9FAFB',
      },
    },
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: '20px',
        minWidth: '360px',
      }}
    >
      {loadingFiles && !initialLoadDone ? (
        <LoadingPlaceholder theme={user?.theme} label={`Loading ${label}...`} />
      ) : (
        <>
          <SearchableSelect
            options={fileOptions}
            label={selectLabel}
            loading={loadingFiles}
            loadingText="Searching..."
            onInputChange={handleInputChange}
            filterOptions={x => x}
            fullWidth
            onChange={value => {
              const fileId = value ? value.value : '';
              setSelectedFileId(fileId);
              setTimeout(() => {
                onChangeSelect({
                  target: { name: fileIdField, value: fileId },
                });
              }, 0);
            }}
            value={fileOptions.find(o => o.value === selectedFileId) || null}
            className="nodrag"
            sx={searchableSelectSx}
            MenuProps={menuPropsSx}
          />

          {children}
        </>
      )}
    </Box>
  );
};

export default MicrosoftFileSelect;
